import { Link } from "wouter";
import { FaInstagram, FaPinterestP, FaLinkedinIn } from "react-icons/fa";
import { SiSubstack } from "react-icons/si";
import {
  FOOTER_COMPANY_LINKS,
  FOOTER_PRODUCT_LINKS,
  SOCIAL_LINKS,
  nw,
} from "@/components/landing/landing-data";
import { FooterWordmark } from "@/components/landing/footer-wordmark";

const SOCIAL_ICONS = {
  Instagram: FaInstagram,
  Pinterest: FaPinterestP,
  LinkedIn: FaLinkedinIn,
  Substack: SiSubstack,
};

/**
 * Homepage-only footer in the V6 layout. Same links, socials and legal line as the
 * shared <Footer /> (still used by /terms, /privacy and /contact, and left untouched).
 */
export function LandingFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="overflow-hidden pb-8 pt-16 md:pt-[90px]">
      <div className="mx-auto max-w-[1360px] px-4 sm:px-8 lg:px-[60px]">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-[minmax(0,2fr)_repeat(2,minmax(0,1fr))] md:gap-12">
          <div className="col-span-2 flex max-w-[380px] flex-col gap-4 md:col-span-1">
            <p className="text-2xl font-bold leading-[1.15] tracking-[-0.03em] text-(--lp-ink) sm:text-[28px]">
              {nw("Where consumption becomes momentum.")}
            </p>
            <p className="text-base leading-relaxed text-(--lp-muted)">
              {nw("Turn what you read, watch, and feel into real change.")}
            </p>
            <ul className="mt-1 flex items-center gap-2">
              {SOCIAL_LINKS.map(({ label, href }) => {
                const Icon = SOCIAL_ICONS[label];
                return (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-(--lp-body) shadow-[0_1px_2px_rgba(47,41,37,0.08)] transition-colors hover:bg-(--lp-lavender) hover:text-(--lp-accent)"
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {[
            { title: "Product", links: FOOTER_PRODUCT_LINKS },
            { title: "Company", links: FOOTER_COMPANY_LINKS },
          ].map((col) => (
            <nav key={col.title} aria-labelledby={`lp-footer-${col.title}`}>
              <h2 id={`lp-footer-${col.title}`} className="mb-3 text-xl font-bold tracking-[-0.02em] text-(--lp-ink) sm:text-[22px]">
                {col.title}
              </h2>
              <ul className="flex flex-col">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link
                      href={l.href}
                      className="inline-flex min-h-11 items-center rounded text-base text-(--lp-ink) transition-colors hover:text-(--lp-accent)"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-2 text-sm text-(--lp-muted) md:mt-[70px] md:flex-row md:justify-between md:gap-6">
          <p>
            © {year} UnfoldWin.com. All rights reserved. A product of{" "}
            <a
              href="https://day0labs.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded font-medium text-(--lp-body) underline underline-offset-2 transition-colors hover:text-(--lp-ink)"
            >
              Day0&nbsp;Labs
            </a>
            .
          </p>
          <p>{nw("Made with care for reflective minds.")}</p>
        </div>

        <div className="mt-10 md:mt-[60px]">
          <FooterWordmark />
        </div>
      </div>
    </footer>
  );
}
